import React from 'react';
import transition from '../../transition';
import CaseStudy from '../CaseStudy/CaseStudy';
import '../Project.css';

import hero from '../../assets/images/projects/dining-with-tara/dining-with-tara-hero.png';
import homeShot from '../../assets/images/projects/dining-with-tara/hero.png';
import googleDoc from '../../assets/images/projects/dining-with-tara/google-doc-before.png';
import homeMobile from '../../assets/images/projects/dining-with-tara/home-mobile.png';
import recipes from '../../assets/images/projects/dining-with-tara/recipe.png';
import recipeDetail from '../../assets/images/projects/dining-with-tara/recipe-detail.png';
import recipeDetail2 from '../../assets/images/projects/dining-with-tara/recipe-detail-2.png';
import recipeDetailMobile from '../../assets/images/projects/dining-with-tara/recipe-detail-mobile.png';
import recipeScaled from '../../assets/images/projects/dining-with-tara/recipe-scaled.png';
import recipeHideImages from '../../assets/images/projects/dining-with-tara/recipe-hide-images.png';
import recipeConsolidate from '../../assets/images/projects/dining-with-tara/recipe-consolidate.png';
import category from '../../assets/images/projects/dining-with-tara/page-https-www-diningwithtara-com-recipes-cat.png';
import featureRecipes from '../../assets/images/projects/dining-with-tara/feature-premium-recipes.jpg';
import featureShopping from '../../assets/images/projects/dining-with-tara/feature-shopping-list.jpg';
import featureAdFree from '../../assets/images/projects/dining-with-tara/feature-ad-free.jpg';

const DiningWithTara = () => {
  return (
    <CaseStudy
      className="diningWithTara"
      accent="var(--diningWithTara)"
      eyebrow="Product · Recipe platform"
      title="Dining with Tara"
      summary="I designed and built a Next.js recipe platform for a functional medicine practice: safe meal discovery, a weekly meal planner, cook tools, and Stripe-powered Premium."
      links={[
        {
          href: 'https://www.diningwithtara.com/',
          label: 'View live site'
        },
        {
          href: '#frames',
          label: 'View frames'
        }
      ]}
      hero={{
        src: hero,
        alt: 'Dining with Tara desktop and mobile recipe platform composition',
        caption: 'Dining with Tara · site + cook mode'
      }}
      date="2024 – 2025"
      role="Sole developer — product, UX, and full stack"
      stack={['Next.js', 'React.js', 'MySQL', 'Stripe', 'JavaScript']}
      brief={{
        label: 'Background',
        they: {
          title: 'The product',
          body: (
            <>
              Dining with Tara is the cooking companion to Tara Quintana’s
              functional medicine practice and{' '}
              <a
                href="https://quintanafunctionalwellness.com/"
                target="_blank"
                rel="noreferrer"
              >
                Quintana Functional Wellness
              </a>
              . Users are patients on elimination or reintroduction diets, plus a
              wider audience from her{' '}
              <a
                href="https://www.youtube.com/@DiningWithTara"
                target="_blank"
                rel="noreferrer"
              >
                YouTube channel
              </a>{' '}
              who need meals they can trust under dietary constraints.
            </>
          )
        },
        me: {
          title: 'My role',
          body: 'Sole developer end to end. I shaped the product, designed the UX, and built the Next.js + MySQL stack: recipe library, meal planner, ingredient-driven diet labels, cook tools, accounts, and Stripe checkout for Premium. Same client family as QFW; this app is dinner, not courses.'
        }
      }}
      problem={{
        title: 'An intense clinical diet with no usable kitchen.',
        lead: 'Tara’s practice often starts care with an elimination diet — temporarily cutting common triggers like gluten, dairy, soy, and more so inflammation can settle and it’s clearer what’s causing symptoms.',
        detail:
          'That protocol is medically useful and personally brutal. Comfort foods disappear overnight. Reintroduction later is slow and careful: one food back at a time while watching symptoms. Patients weren’t failing in the exam room; they were failing at 6pm with an empty fridge and no ideas that fit the rules. Tara’s answer was a Google Doc of meals and PDF “inspo” cards emailed from clinic. Those proved restricted eating wasn’t a life sentence of sad salads, but they didn’t search, didn’t scale for a family, and couldn’t keep diet claims honest as recipes changed. The product job was to make that intense protocol cookable — including planning a whole week, not just finding one recipe.',
        image: {
          src: googleDoc,
          alt: 'Tara’s Google Doc of breakfast and lunch recipes before the website',
          caption: 'The infamous Google Doc — before the app'
        }
      }}
      metrics={[
        { value: 'Planner', label: 'Weekly meal plans + grocery PDF' },
        { value: 'Stripe', label: 'Premium checkout built in' },
        { value: 'Auto', label: 'Diet labels from ingredients' },
        { value: '½x–4x', label: 'Servings rewrite list + steps' }
      ]}
      outcome="A live platform that replaced the Doc and PDFs: searchable recipes, a meal planner with shopping lists, cook tools, and paid Premium through Stripe."
      solutionsLabel="What I built"
      solutions={[
        {
          title: 'Recipe platform from scratch',
          lead: 'Public site + recipe library that patients and home cooks can actually navigate.',
          detail:
            'Homepage, categories, search, recipe detail, about, and blog. Replaced the Google Doc grid with a real content model in MySQL so the practice can grow the library without starting a new attachment chain. Mobile was first-class: most people open this in a grocery aisle or at the stove.',
          images: [
            {
              src: homeShot,
              alt: 'Dining with Tara homepage',
              caption: 'After: a real product home.'
            },
            {
              src: recipes,
              alt: 'Recipes index',
              caption: 'Searchable library instead of a Doc.'
            },
            {
              src: category,
              alt: 'Category-filtered recipes',
              caption: 'Browse by diet and category.'
            },
            {
              src: homeMobile,
              alt: 'Homepage on mobile',
              caption: 'Phone-first layout.'
            }
          ]
        },
        {
          title: 'Ingredient-driven diet labels',
          lead: 'Gluten-free and similar claims are computed from ingredients, not manual tags.',
          detail:
            'On a restricted diet, a wrong label is a real failure. Labels recalculate when a recipe changes, so browsing for safe meals stays honest. Not every recipe is forced into a diet box — the library serves protocol patients and people who just want good food.',
          images: [
            {
              src: recipes,
              alt: 'Recipe library browse',
              caption: 'Diet truth baked into the data model.'
            },
            {
              src: recipeDetail,
              alt: 'Recipe detail page',
              caption: 'Labels follow the ingredient list.'
            },
            {
              src: recipeDetail2,
              alt: 'Another recipe detail page',
              caption: 'Edit later; claims update.'
            }
          ]
        },
        {
          title: 'Smart servings',
          lead: '½x–4x updates the ingredient list and quantities written inside the steps.',
          detail:
            'Scaling that only touches the bullet list still leaves “add 6 garlic cloves” wrong in Step 3. The engine rewrites amounts in the method prose too, so family batches don’t require mental math mid-cook.',
          images: [
            {
              src: recipeDetail,
              alt: 'Recipe detail with servings controls',
              caption: 'Serving controls on the recipe page.'
            },
            {
              src: recipeScaled,
              alt: 'Recipe scaled to 2x servings',
              caption: 'Scaled batch across list and steps.'
            },
            {
              src: recipeDetailMobile,
              alt: 'Recipe detail on mobile',
              caption: 'Usable one-handed at the counter.'
            }
          ]
        },
        {
          title: 'Cook-mode UX',
          lead: 'Hide images, keep the screen awake, consolidate ingredients, print from live data.',
          detail:
            'Built for greasy fingers, not blog scrolling. Cook mode, instruction-first view, shopping-friendly consolidate, and print that pulls from the current recipe instead of a stale PDF.',
          images: [
            {
              src: recipeHideImages,
              alt: 'Recipe with images hidden',
              caption: 'Instructions-first cooking view.'
            },
            {
              src: recipeConsolidate,
              alt: 'Consolidated ingredients list',
              caption: 'One list for shopping.'
            },
            {
              src: recipeDetailMobile,
              alt: 'Mobile cooking view',
              caption: 'Phone up, burner on.'
            }
          ]
        },
        {
          title: 'Meal planner',
          lead: 'A weekly builder that turns the library into an actual week of dinners — then a grocery list.',
          detail:
            'Users build meal plans day by day from the recipe library (and their own custom recipes), adjust servings per slot, and manage multiple plans. From a plan they can generate a shopping-list PDF so the store trip matches the week, not a screenshot of a Doc. For people on elimination diets, planning the week is half the battle; improvising at 6pm is how protocols break.',
          images: [
            {
              src: featureShopping,
              alt: 'Shopping list generated from a meal plan',
              caption: 'Auto grocery list from the week’s plan.'
            },
            {
              src: featureRecipes,
              alt: 'Premium recipe library access',
              caption: 'Planner pulls from the full recipe set.'
            },
            {
              src: featureAdFree,
              alt: 'Focused cooking and planning experience',
              caption: 'Planning and cooking without the clutter.'
            }
          ]
        },
        {
          title: 'Premium + Stripe',
          lead: 'Accounts, gated Premium features, and checkout through Stripe — not a separate payment site.',
          detail:
            'Free browsing covers the public kitchen. Premium unlocks the heavier tools: more meal plans per week, premium recipes and filters, custom recipes, weekly nutrition insights, shopping lists, and an ad-free experience. Checkout launches Stripe from inside the app, and Premium status is tied to the user account so access stays in-product instead of living in email receipts.'
        }
      ]}
      narrative={[
        {
          title: 'How it fits the practice',
          lead: 'Same care world as Quintana Functional Wellness — different job.',
          detail:
            'QFW handles courses and education. Dining with Tara is the kitchen: recipe data, diet-safe browsing, weekly meal planning, cook tools, and Premium through Stripe. Built end to end so patients leave with a week they can actually cook, not another PDF in their inbox.'
        }
      ]}
      decisions={[
        {
          title: 'Compute diet labels from ingredients',
          body: 'Manual tags drift. Ingredient-driven claims stay true when recipes change — critical when wrong data can make someone sick.'
        },
        {
          title: 'Plan the week, not just one recipe',
          body: 'Elimination diets fail between Sundays. The meal planner and grocery PDF exist so patients leave clinic with a cookable week, not a single PDF card.'
        },
        {
          title: 'Stripe inside the product',
          body: 'Premium checkout stays on the app’s rails. Users subscribe without bouncing to a disconnected storefront the practice can’t tie back to accounts and feature gates.'
        },
        {
          title: 'Scale prose, not just the list',
          body: 'Quantities live in the method text. Updating only bullets would leave the cook with conflicting numbers mid-recipe.'
        }
      ]}
      next={{ link: '/projects/bintibiz', label: 'Binti Biz' }}
    />
  );
};

export default transition(DiningWithTara);
